import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { Subject, tap } from 'rxjs';
import { awaitLoading } from '../../shared/rxjs/await-loading';
import { MutationResult } from 'apollo-angular';
import {
    AddShiftsGQL,
    AddShiftsMutation,
    CalendarCrewModalDataDocument,
    ChangeShiftDurationGQL,
    DeleteShiftGQL,
    OverlappingShiftsByDateRangeGQL,
} from '../../../../graphql/generated';


@Injectable({
    providedIn: 'root',
})
export class ShiftService {

    private readonly _created = new Subject<MutationResult<AddShiftsMutation>>();
    public readonly created$ = this._created.asObservable();

    private readonly _deleted = new Subject<string>();
    public readonly deleted$ = this._deleted.asObservable();

    constructor(
        private readonly getOverlappingShiftsQueryGQL: OverlappingShiftsByDateRangeGQL,
        private readonly deleteShiftGQL: DeleteShiftGQL,
        private readonly addShiftsGQL: AddShiftsGQL,
        private readonly changeShiftDurationGQL: ChangeShiftDurationGQL,
    ) {
    }

    public createShifts(crewId: string, employeeIds: string[]) {
        return this.addShiftsGQL.mutate({ input: { crewId, employeeIds } }).pipe(
            awaitLoading(),
            tap(shifts => this._created.next(shifts)),
        );
    }

    public changeShiftDuration(shiftId: string, duration: string) {
        return this.changeShiftDurationGQL.mutate({
            input: {
                shiftId,
                duration,
            },
        }, {
            refetchQueries: [
                CalendarCrewModalDataDocument,
            ],
        }).pipe(
            awaitLoading(),
            map(({ data }) => data.changeShiftDuration.success),
        );
    }

    public deleteShift(shiftId: string) {
        return this.deleteShiftGQL.mutate({ shiftId }).pipe(
            awaitLoading(),
            map(({ data }) => data.deleteShift.deletedShiftId),
            tap(id => this._deleted.next(id)),
        );
    }

    public getOverlappingShiftsByDateRange(startDate: Date, endDate: Date) {
        return this.getOverlappingShiftsQueryGQL.watch({
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
        }, {
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            awaitLoading(),
            map(({ data }) => data.overlappingShiftsByDateRange as { id: string }[]),
        );
    }

}
